import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Check, Circle, ArrowRight, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";

type TrackKey = "advocate" | "partner" | "anchor";

type Step = {
  key: string;
  label: string;
  helper?: string;
  done: boolean;
  action?: { label: string; href: string };
};

function useChecklist(orgId: string, orgSlug: string, orgTier: string) {
  return useQuery({
    queryKey: ["partner-checklist", orgId],
    queryFn: async () => {
      const [org, mou, interests, matches, outcomes, clicks, leads, commitments] = await Promise.all([
        supabase.from("affiliates").select("id, name, description, logo_url, website, referral_code, status, tier").eq("id", orgId).maybeSingle(),
        supabase.from("affiliates").select("mou_accepted_at").eq("id", orgId).maybeSingle(),
        supabase.from("idea_partner_interests").select("id").eq("partner_org_id", orgId).limit(1),
        supabase.from("partner_match_routes").select("id, status").eq("affiliate_id", orgId),
        supabase.from("partner_outcomes_reports").select("id").eq("affiliate_id", orgId).limit(1),
        supabase.from("affiliate_clicks").select("id", { count: "exact", head: true }).eq("affiliate_id", orgId),
        supabase.from("affiliate_leads").select("id", { count: "exact", head: true }).eq("affiliate_id", orgId),
        supabase.from("anchor_commitments").select("id, is_published").eq("affiliate_id", orgId),
      ]);
      return {
        org: org.data,
        mouAccepted: !!(mou.data as any)?.mou_accepted_at,
        hasInterest: (interests.data ?? []).length > 0,
        acceptedMatches: (matches.data ?? []).filter((m: any) => m.status === "accepted" || m.status === "in_progress" || m.status === "completed").length,
        hasOutcome: (outcomes.data ?? []).length > 0,
        clicks: clicks.count ?? 0,
        leads: leads.count ?? 0,
        commitmentCount: (commitments.data ?? []).length,
        publishedCommitments: (commitments.data ?? []).filter((c: any) => c.is_published).length,
      };
    },
  });
}

function trackFor(tier: string): TrackKey {
  if (tier === "advocate") return "advocate";
  if (tier === "anchor") return "anchor";
  return "partner";
}

function buildSteps(track: TrackKey, d: any, orgSlug: string): Step[] {
  const profileComplete = !!(d.org?.description && d.org?.website);
  const advocate: Step[] = [
    { key: "profile", label: "Complete your public profile", helper: "Description + website filled in", done: profileComplete, action: { label: "Edit profile", href: "#" } },
    { key: "code", label: "Claim your referral code", helper: d.org?.referral_code ? `Code: ${d.org.referral_code}` : "Auto-generated on approval", done: !!d.org?.referral_code },
    { key: "share", label: "Post your first referral", helper: "Use the LinkedIn / email scripts from the playbook", done: d.clicks > 0 },
    { key: "click", label: "First click tracked", helper: `${d.clicks} click${d.clicks === 1 ? "" : "s"} so far`, done: d.clicks > 0 },
    { key: "lead", label: "First lead attributed", helper: `${d.leads} lead${d.leads === 1 ? "" : "s"} so far`, done: d.leads > 0 },
  ];

  const partner: Step[] = [
    { key: "mou", label: "Sign the partner MOU", helper: "Click-to-accept in the Agreement tab", done: d.mouAccepted },
    { key: "profile", label: "Publish your org profile", helper: "Description, website, services", done: profileComplete },
    { key: "interest", label: "Express interest on your first idea", helper: "Browse the Ideas Board and open a thread", done: d.hasInterest, action: { label: "Ideas Board", href: "/ideas" } },
    { key: "match", label: "Accept your first routed match", helper: `${d.acceptedMatches} accepted`, done: d.acceptedMatches > 0 },
    { key: "outcome", label: "File a quarterly outcomes report", helper: d.hasOutcome ? "Latest report on file" : "Due at end of each quarter", done: d.hasOutcome },
  ];

  const anchor: Step[] = [
    ...partner,
    { key: "anchor_page", label: "Publish your anchor landing page", helper: `${d.publishedCommitments} published item${d.publishedCommitments === 1 ? "" : "s"}`, done: d.publishedCommitments > 0, action: { label: "Anchor Page tab", href: `/anchors/${orgSlug}` } },
    { key: "commitment", label: "List your first commitment", done: d.commitmentCount > 0 },
  ];

  return track === "advocate" ? advocate : track === "anchor" ? anchor : partner;
}

export default function OnboardingChecklistTab({ orgId, orgSlug, orgTier }: { orgId: string; orgSlug: string; orgTier: string }) {
  const { data, isLoading } = useChecklist(orgId, orgSlug, orgTier);

  if (isLoading || !data) {
    return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const track = trackFor(orgTier);
  const steps = buildSteps(track, data, orgSlug);
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / steps.length) * 100);
  const nextStep = steps.find((s) => !s.done);

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Onboarding track</div>
            <div className="font-display text-xl mt-0.5">
              {track === "advocate" ? "Advocate" : track === "anchor" ? "Anchor" : "Partner"} · {done}/{steps.length} complete
            </div>
          </div>
          <Badge variant="outline">{pct}%</Badge>
        </div>
        <Progress value={pct} className="h-2" />
        {nextStep && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 p-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-primary">Next best step</div>
              <div className="text-sm font-medium mt-0.5">{nextStep.label}</div>
              {nextStep.helper && <div className="text-xs text-muted-foreground mt-0.5">{nextStep.helper}</div>}
            </div>
            {nextStep.action && (
              <Button asChild size="sm" variant="outline">
                <Link to={nextStep.action.href}>{nextStep.action.label} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
              </Button>
            )}
          </div>
        )}
      </Card>

      <Card className="divide-y divide-border">
        {steps.map((s) => (
          <div key={s.key} className="p-4 flex items-start gap-3">
            <div className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center border ${
              s.done ? "bg-primary border-primary text-primary-foreground" : "border-border text-muted-foreground"
            }`}>
              {s.done ? <Check className="h-3 w-3" /> : <Circle className="h-2 w-2" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.label}</div>
              {s.helper && <div className="text-xs text-muted-foreground mt-0.5">{s.helper}</div>}
            </div>
            {s.action && !s.done && (
              <Button asChild size="sm" variant="ghost">
                <Link to={s.action.href}>{s.action.label}</Link>
              </Button>
            )}
          </div>
        ))}
      </Card>

      <div className="text-xs text-muted-foreground">
        Auto-checked from your live account activity. See the full{" "}
        <Link to="/partners/handbook" className="text-primary hover:underline">Partner &amp; Advocate Playbook</Link>{" "}
        for scripts, scoping guardrails, and outcomes storytelling.
      </div>
    </div>
  );
}